import type { AxiosError } from 'axios';
import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import { RemitFormPayload } from '@/forms/RemitForm/types';
import { Remit, NewRemitOptions } from '@/types/billing';
import PaginatedResponse from '@/types/paginatedResponse';
import type RepositoryResponse from '@/types/repositoryResponse';
import ajax from '@/utils/ajax';
import defaultRepositoryError from '@/utils/defaultRepositoryError';
import logAjaxError from '@/utils/logAjaxError';

export const remitRepository = {
  async getRemitByCheckNumber({
    host,
    checkNumber
  }: {
    host: string;
    checkNumber: string;
  }): Promise<RepositoryResponse<Remit>> {
    try {
      const path = `/api/billing/remits/by_check_number/${checkNumber}/`;
      const response = await ajax.get({
        url: `${host}${path}`
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to get Remit by Check Number.');
    }
  },

  async listRemits({
    host,
    params
  }: {
    host: string;
    params: ListRemitsParams;
  }): Promise<RepositoryResponse<PaginatedResponse<Remit[]>>> {
    try {
      const path = `/api/billing/remits/`;
      const response = await ajax.get({
        url: `${host}${path}`,
        params
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to list Remits.');
    }
  },

  async getRemit({
    host,
    remitID
  }: {
    host: string;
    remitID: string;
  }): Promise<RepositoryResponse<Remit>> {
    try {
      const path = `/api/billing/remits/${remitID}/`;
      const response = await ajax.get({
        url: `${host}${path}`
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to get Remit.');
    }
  },

  async getNewRemit(host: string): Promise<RepositoryResponse<NewRemitOptions>> {
    try {
      const path = `/api/billing/remits/new/`;
      const response = await ajax.get({
        url: `${host}${path}`
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to get new Remit options.');
    }
  },

  async updateRemit({
    host,
    params,
    remitId
  }: {
    host: string;
    params: Partial<RemitFormPayload>;
    remitId: number;
  }): Promise<RepositoryResponse<Remit>> {
    try {
      const path = `/api/billing/remits/${remitId}/`;
      const response = await ajax.patch({
        url: `${host}${path}`,
        data: params
      });

      return {
        data: response.data as unknown as Remit,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to update Remit.');
    }
  },

  async createRemit({
    host,
    params
  }: {
    host: string;
    params: Partial<RemitFormPayload>;
  }): Promise<RepositoryResponse<Remit>> {
    try {
      const path = `/api/billing/remits/`;
      const response = await ajax.post({
        url: `${host}${path}`,
        data: params
      });

      return {
        data: response.data as unknown as Remit,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to create Remit.');
    }
  }
};

export interface ListRemitsParams {
  [key: string]: string | number | undefined | null;
  page?: number | string;
  pageSize?: number | string;
  cms1500ID?: string;
  ordering?: string;
  search?: string;
  type?: string;
  status?: string;
  ingested_from?: string;
  ingested_at?: string;
  ingested_at__gte?: string;
  ingested_at__lte?: string;
  transmission_on?: string;
  transmission_on__gte?: string;
  transmission_on__lte?: string;
}

const RemitRepository = () => {
  const { host } = useContext(AppContext);

  return {
    async getRemitByCheckNumber({
      checkNumber
    }: {
      checkNumber: string;
    }): Promise<RepositoryResponse<Remit>> {
      return remitRepository.getRemitByCheckNumber({ host, checkNumber });
    },

    async listRemits(
      params: ListRemitsParams = {}
    ): Promise<RepositoryResponse<PaginatedResponse<Remit[]>>> {
      return remitRepository.listRemits({ host, params: params });
    },

    async getRemit({ remitID }: { remitID: string }): Promise<RepositoryResponse<Remit>> {
      return remitRepository.getRemit({ host, remitID });
    },
    async getNewRemit(): Promise<RepositoryResponse<NewRemitOptions>> {
      return remitRepository.getNewRemit(host);
    },
    async updateRemit({
      params,
      remitId
    }: {
      params: Partial<RemitFormPayload>;
      remitId: number;
    }): Promise<RepositoryResponse<Partial<Remit>>> {
      return remitRepository.updateRemit({ host, params, remitId });
    },
    async createRemit({
      params
    }: {
      params: Partial<RemitFormPayload>;
    }): Promise<RepositoryResponse<Partial<Remit>>> {
      return remitRepository.createRemit({ host, params });
    }
  };
};

export default RemitRepository;
